import React, { useId, useState } from "react";

import { faChevronDown, faChevronUp, faCircleCheck, faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Text } from "@performyard/py-components";

import type { GoalImprovementStatus } from "../types";

/** The card only reads these fields, so goal and form improvements both render through it. */
export type ImprovementCardSuggestion = {
	id: string;
	title: string;
	rationale: string;
	suggestedValue: string;
	/** The question the change targets, named under the change itself. Only the form-authoring coach
	 *  carries one — a goal-field improvement has no question to name. */
	questionTitle?: string;
};

export type ImprovementCardState = {
	suggestion: ImprovementCardSuggestion;
	status: GoalImprovementStatus;
	/** Rebuilt from saved history — shown, never applied. */
	isRestored?: boolean;
};

type ImprovementCardProps = {
	suggestionState: ImprovementCardState;
	onApply: () => void;
	/** Whether the page this panel is open on can actually take the change right now (PY-14326). */
	canApply: boolean;
};

export const ImprovementCard: React.FC<ImprovementCardProps> = ({ suggestionState, onApply, canApply }) => {
	const { suggestion, status, isRestored } = suggestionState;
	const [isRationaleOpen, setIsRationaleOpen] = useState(false);
	const rationaleId = useId();

	if (status === "deleted") {
		return null;
	}

	// A restored card keeps its Applied confirmation when the change really landed —
	// only an unapplied one has nothing to show, so its Apply button just disappears
	// below (see Figma node 426:7223, "History / One Applied").
	const isApplied = status === "applied";

	return (
		<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-0 px-3 py-4">
			{/* Node 38:22344: the reason for the suggestion, then the change itself, tight together —
			    the question and the rationale are context for that pair, an 8px gap below. */}
			<div className="flex max-w-full min-w-0 flex-col gap-1">
				{/* Two lines is the most the reason slot can hold; the server trims the shape the model
				    over-writes, and this bounds whatever still gets through. */}
				<Text
					size="caption"
					color="neutral-7"
					dangerouslySetClassName="line-clamp-2 min-w-0 break-words uppercase"
				>
					{suggestion.title}
				</Text>
				<Text
					size="paragraph-3"
					weight="semi-bold"
					color="neutral-9"
					dangerouslySetClassName="min-w-0 whitespace-pre-wrap break-words"
				>
					{`"${suggestion.suggestedValue}"`}
				</Text>
			</div>

			{suggestion.questionTitle && (
				<Text size="caption" color="neutral-7" dangerouslySetClassName="min-w-0 break-words">
					{`Question: ${suggestion.questionTitle}`}
				</Text>
			)}

			{/* Node 38:22352, the "Explanation Accordion": a text button on a 16px row, so the label
			    sits flush left at the card's inset rather than centered across the full width. */}
			<div className="flex min-w-0 flex-col gap-1">
				<ButtonStandard
					variant="text"
					color="primary"
					size="small"
					iconRight={isRationaleOpen ? faChevronUp : faChevronDown}
					onClick={() => setIsRationaleOpen(!isRationaleOpen)}
					aria-expanded={isRationaleOpen}
					aria-controls={rationaleId}
					dangerouslySetClassName="h-4 w-auto justify-start"
				>
					See Why
				</ButtonStandard>
				{isRationaleOpen && (
					<Text
						id={rationaleId}
						size="caption"
						color="neutral-7"
						dangerouslySetClassName="min-w-0 break-words"
					>
						{suggestion.rationale}
					</Text>
				)}
			</div>

			{isApplied ? (
				<ButtonStandard
					variant="outline"
					color="green"
					size="small"
					iconLeft={faCircleCheck}
					onClick={() => undefined}
					dangerouslySetClassName="pointer-events-none cursor-default"
				>
					Change Applied
				</ButtonStandard>
			) : (
				// Hidden when restored (can't become actionable) or when the page this
				// panel is open on isn't the one the change applies to — either way,
				// there's nothing this button could actually do right now (PY-14326).
				!isRestored &&
				canApply && (
					<ButtonStandard
						variant="outline"
						color="primary"
						size="small"
						iconLeft={faPlus}
						onClick={onApply}
						data-testid="askpy-apply-change-button"
					>
						Apply Change
					</ButtonStandard>
				)
			)}
		</div>
	);
};

export default ImprovementCard;
